import { useMemo, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { BROKER_WORK_AREAS, parseBrokerAreas } from '../utils/brokerAreas';

type Props = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  frontDeskStyle?: boolean;
};

export default function BrokerAreaPicker({value, onChange, label = 'Areas you work in',
  frontDeskStyle = false}: Props) {
  const [inputValue, setInputValue] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const selected = useMemo(() => parseBrokerAreas(value), [value]);
  // Preserve areas already stored on older broker records, even if they are not
  // part of the current suggestion list.
  const options = useMemo(() => [...BROKER_WORK_AREAS, ...selected.filter((area) =>
    !BROKER_WORK_AREAS.some((option) => option.toLocaleLowerCase() === area.toLocaleLowerCase()))],
  [selected]);

  return <Box sx={{width: '100%', minWidth: 0}}>
    {frontDeskStyle && <Typography component="label" sx={{display: 'block', mb: 0.75,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: 13, fontWeight: 600, color: '#5b5b5b'}}>{label}</Typography>}
    <Autocomplete<string, true, false, false>
    multiple
    filterSelectedOptions
    forcePopupIcon={false}
    options={options}
    open={searchOpen && inputValue.trim().length >= 2}
    onClose={() => setSearchOpen(false)}
    inputValue={inputValue}
    onInputChange={(_, nextInput) => {
      setInputValue(nextInput);
      setSearchOpen(nextInput.trim().length >= 2);
    }}
    filterOptions={(available, state) => available.filter((area) =>
      area.toLocaleLowerCase().includes(state.inputValue.trim().toLocaleLowerCase()))}
    noOptionsText="No matching areas"
    value={selected}
    onChange={(_, areas) => {
      onChange(areas.join(', '));
      setInputValue('');
      setSearchOpen(false);
    }}
    renderInput={(params) => <TextField {...params} label={frontDeskStyle ? undefined : label}
      placeholder={selected.length ? '' : 'Type 2 letters to search'}
      helperText={frontDeskStyle ? undefined : 'Choose all areas where you work'} />}
    sx={{width: '100%', '& .MuiAutocomplete-tag': {bgcolor: '#f4eee5', color: '#714c1e'},
      ...(frontDeskStyle ? {
        '& .MuiOutlinedInput-root': {minHeight: 46, py: '2px', borderRadius: '10px', bgcolor: '#fff'},
        '& .MuiOutlinedInput-notchedOutline': {borderColor: '#d9d3c8'},
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#9d6f32', borderWidth: 2,
        },
        '& .MuiInputBase-input': {fontSize: 16},
      } : {})}}
    />
  </Box>;
}
